import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clapperboard, Check, X, Send, RefreshCw, Download, Gamepad2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AutopilotSettings } from "@/components/AutopilotSettings";

const STATUS_COLOR = { pending: "text-[#FF9F0A]", approved: "text-[#007AFF]", published: "text-[#34C759]", rejected: "text-neutral-500" };

export function OfficialStagePanel() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [kind, setKind] = useState("");
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/official/queue", { params: { ...(filter ? { status: filter } : {}), ...(kind ? { kind } : {}), limit: 200 } });
      setData(data); setSel([]);
    } catch { /* admin only */ }
  }, [filter, kind]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    window.addEventListener("vismarks:channels-changed", onFocus);
    return () => { window.removeEventListener("focus", onFocus); window.removeEventListener("vismarks:channels-changed", onFocus); };
  }, [load]);

  const act = async (action, ids) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/official/decide", { ids, action });
      toast.success(`${data.done} piece${data.done === 1 ? "" : "s"} ${action === "publish" ? "published" : action === "reject" ? "rejected" : "approved"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const generate = async () => {
    setBusy(true);
    try { await api.post("/official/generate"); toast.success("Planning today's pieces in all languages — they appear here as they're written and rendered"); setTimeout(load, 8000); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const pack = async () => {
    try {
      const r = await api.get("/official/whatsapp-pack", { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = "vismarks-whatsapp-pack.zip"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return null;
  const items = data.items || [];
  const c = data.counts || {};
  const ch = data.channels || {};
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div data-testid="official-stage-panel" className="mt-10 rounded-xl border border-[#27272A] bg-[#121212] p-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#FF3B30]"><Clapperboard className="h-4 w-4" /> Official VISMARKS Stage</div>
          <h2 className="mt-1 font-black text-xl text-white">Daily content autopilot</h2>
          <p className="text-sm text-neutral-400 mt-1 max-w-2xl">
            Wise sayings, flyers and audience games are generated every day. Approve what you like — anything untouched auto-publishes after 24h. Published pieces go to the{" "}
            <Link to={`/store/${data.official_seller_id}`} className="underline hover:text-white" data-testid="official-store-link">Official Stage</Link>
            {" "}and to every connected channel.
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-[11px]" data-testid="official-channels">
            {[["telegram", "Telegram"], ["x", "X / Twitter"], ["facebook_instagram", "Facebook & Instagram"], ["whatsapp_pack", "WhatsApp pack"]].map(([k, l]) => (
              <span key={k} className={`px-2 py-0.5 rounded-full border ${ch[k] ? "border-[#34C759]/40 text-[#34C759]" : "border-white/10 text-neutral-500"}`}>{l}: {ch[k] ? "connected" : "not set"}</span>
            ))}
          </div>
          {data.last_run && <div className="text-[11px] text-neutral-500 mt-1">Last autopilot run: {new Date(data.last_run).toLocaleString()}</div>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="official-generate-btn" onClick={generate} disabled={busy} variant="outline" className="rounded-full border-white/20"><RefreshCw className="h-4 w-4 mr-1" /> Generate today</Button>
          <Button data-testid="official-pack-btn" onClick={pack} variant="outline" className="rounded-full border-white/20"><Download className="h-4 w-4 mr-1" /> WhatsApp pack</Button>
        </div>
      </div>

      <AutopilotSettings onChanged={load} />

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {["saying", "flyer", "video", "clip", "game", ""].map((k) => (
          <button key={k || "allk"} data-testid={`official-kind-${k || "all"}`} onClick={() => setKind(k)}
            className={`px-2.5 py-1 rounded-full text-[11px] border ${kind === k ? "border-[#FF3B30] text-[#FF3B30]" : "border-white/10 text-neutral-400 hover:border-white/30"}`}>{k || "all types"}</button>
        ))}
        <span className="w-px h-5 bg-white/10 mx-1" />
        {["pending", "approved", "published", "rejected", ""].map((s) => (
          <button key={s || "all"} data-testid={`official-filter-${s || "all"}`} onClick={() => setFilter(s)}
            className={`px-3 py-1 rounded-full text-xs border ${filter === s ? "border-white bg-white text-black" : "border-white/15 text-neutral-300 hover:border-white/40"}`}>
            {s || "all"} {s && c[s] ? `(${c[s]})` : ""}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          <Button size="sm" data-testid="official-approve-btn" disabled={busy || !sel.length} onClick={() => act("approve", sel)} className="rounded-full bg-[#007AFF] hover:bg-[#0066d6] text-white"><Check className="h-4 w-4 mr-1" /> Approve ({sel.length})</Button>
          <Button size="sm" data-testid="official-publish-btn" disabled={busy || !sel.length} onClick={() => act("publish", sel)} className="rounded-full bg-[#34C759] hover:bg-[#2db350] text-black"><Send className="h-4 w-4 mr-1" /> Publish now</Button>
          <Button size="sm" data-testid="official-reject-btn" disabled={busy || !sel.length} onClick={() => act("reject", sel)} variant="outline" className="rounded-full border-white/20"><X className="h-4 w-4 mr-1" /> Reject</Button>
        </div>
      </div>

      {items.length === 0 ? (
        <p className="mt-6 text-sm text-neutral-500" data-testid="official-empty">Nothing here. Tap “Generate today” to create the day's pieces.</p>
      ) : (
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="official-grid">
          {items.map((it) => (
            <button key={it.id} data-testid={`official-item-${it.id}`} onClick={() => toggle(it.id)}
              className={`text-left rounded-xl overflow-hidden border transition-colors ${sel.includes(it.id) ? "border-[#FF3B30]" : "border-white/10 hover:border-white/30"} bg-black/30`}>
              {it.image_url ? (
                <div className="relative">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${it.image_url}`} alt={it.title} className="aspect-square w-full object-cover" loading="lazy" />
                  {it.media_file_id && <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/70 text-[10px] text-white font-bold">▶ {it.duration ? `${Math.round(it.duration)}s` : "video"}</span>}
                  {it.lang && it.lang !== "en" && <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded bg-white/90 text-[10px] text-black font-bold uppercase">{it.lang}</span>}
                </div>
              ) : it.render === "queued" || it.render === "rendering" ? (
                <div className="aspect-square w-full flex flex-col items-center justify-center gap-2 bg-[#1a1a1a] text-neutral-400 p-3 text-center">
                  <RefreshCw className={`h-5 w-5 ${it.render === "rendering" ? "animate-spin text-[#FF9F0A]" : ""}`} /><span className="text-[11px] line-clamp-3">{it.title}</span><span className="text-[10px] uppercase tracking-wider">{it.render === "rendering" ? "rendering" : "waiting to render"}</span>
                </div>
              ) : it.render === "failed" ? (
                <div className="aspect-square w-full flex flex-col items-center justify-center gap-2 bg-[#2a1414] text-[#FF3B30] p-3 text-center"><X className="h-5 w-5" /><span className="text-[11px] line-clamp-3">{it.render_error || "render failed"}</span></div>
              ) : (
                <div className="aspect-square w-full flex flex-col items-center justify-center gap-2 bg-[#1a1a1a] text-neutral-300 p-3 text-center">
                  <Gamepad2 className="h-6 w-6 text-[#FF9F0A]" /><span className="text-xs line-clamp-3">{it.game?.question || it.title}</span>
                </div>
              )}
              <div className="p-2">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-wider"><span className="text-neutral-500">{it.kind}{it.lang && it.lang !== "en" ? ` · ${it.lang}` : ""}{it.social ? " · social" : ""}</span><span className={STATUS_COLOR[it.status]}>{it.status}</span></div>
                {it.scheduled_for && it.status !== "published" && <div className="text-[10px] text-neutral-500">{new Date(it.scheduled_for).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>}
                <div className="text-xs text-neutral-200 line-clamp-2 mt-0.5">{it.title}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
